import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';

export type SupportedLanguage = 'de-AT' | 'it';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly STORAGE_KEY = 'app-language';
  private readonly DEFAULT_LANGUAGE: SupportedLanguage = 'de-AT';

  constructor(private translateService: TranslateService) {}

  /**
   * Initialize language based on browser settings or stored preference
   */
  initializeLanguage(): SupportedLanguage {
    // Check if language is stored in localStorage
    const storedLanguage = localStorage.getItem(this.STORAGE_KEY) as SupportedLanguage;
    if (storedLanguage && (storedLanguage === 'de-AT' || storedLanguage === 'it')) {
      this.setLanguage(storedLanguage);
      return storedLanguage;
    }

    // Detect browser language
    const browserLang = navigator.language || (navigator as any).userLanguage;
    let detectedLanguage: SupportedLanguage = this.DEFAULT_LANGUAGE;

    if (browserLang && browserLang.toLowerCase().startsWith('it')) {
      detectedLanguage = 'it';
    }

    this.setLanguage(detectedLanguage);
    return detectedLanguage;
  }

  /**
   * Set the application language
   */
  setLanguage(language: SupportedLanguage): void {
    // Store in localStorage
    localStorage.setItem(this.STORAGE_KEY, language);

    // Set translate service language
    this.translateService.use(language);

    // Update moment.js locale
    if (language === 'it') {
      moment.locale('it');
    } else {
      moment.locale('de-AT');
    }
  }

  /**
   * Get current language
   */
  getCurrentLanguage(): SupportedLanguage {
    const stored = localStorage.getItem(this.STORAGE_KEY) as SupportedLanguage;
    return stored && (stored === 'de-AT' || stored === 'it') ? stored : this.DEFAULT_LANGUAGE;
  }

  /**
   * Get Angular locale ID (for LOCALE_ID provider)
   */
  getAngularLocale(): string {
    const lang = this.getCurrentLanguage();
    return lang === 'it' ? 'it' : 'de-AT';
  }

  /**
   * Extracts the translated text from a pipe-separated string.
   * Format: "German Text | Italian Text"
   * - If no pipe exists, returns the original string
   * - German (de-AT) uses index 0, Italian (it) uses index 1
   * - Safe access with fallback to the other language if the requested index doesn't exist
   */
  getTranslatedFieldName(value: string): string {
    if (!value || !value.includes('|')) {
      return value || '';
    }

    const parts = value.split('|').map((p) => p.trim());
    const isItalian = this.getCurrentLanguage() === 'it';
    const index = isItalian ? 1 : 0;

    // Safe access: fallback to the other language if requested index doesn't exist
    return parts[index] ?? parts[0] ?? value;
  }
}


